import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Play, LogOut, Clock, AlertTriangle, Home as HomeIcon, Settings, Users, ChevronLeft, ChevronRight, Sun, Moon, Search, BookOpen, Radio, Palette, Film, Sparkles, BedDouble } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useSpatialNav, useWakeLock } from "../../lib/useSpatialNav";
import { KidsSearch, KidsFlashcards, KidsSleepTimer } from "../../components/KidsOverlays";
import { JrPodcastStudio, KidsDraw, KidsBedtime, KidsShorts } from "../../components/KidsActivities";

const PROFILE_STORAGE_KEY = "tapin_kids_profile_id";

/**
 * TvKidsApp — dedicated kids-safe TV experience at /tv/kids.
 *
 * Contract:
 *   · Profile picker shown first when more than one kid profile exists
 *     (Netflix/Disney+ Kids style). Single-profile or no-profile parents
 *     jump straight to the kids home.
 *   · Loads /api/tv/kids-home (strict allowlist — only is_kids_safe=true)
 *   · All chat / voting / comments / store CTAs are intentionally absent
 *   · Exit button gated on /api/kids/verify-pin if a PIN is set
 *   · Polls /api/kids/log-watch every ~30s scoped to the selected profile;
 *     locks out the child UI when the daily limit is hit.
 */
export default function TvKidsApp() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [home, setHome] = useState({ hero: [], rails: [], live_now: [], coming_soon: [] });
  const [cfg, setCfg] = useState({ pin_set: false, remaining_min: 60, locked: false, in_window: true, today_watched_min: 0, daily_limit_min: 60 });
  const [profiles, setProfiles] = useState([]);
  const [profilesLoaded, setProfilesLoaded] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState(() => {
    try { return localStorage.getItem(PROFILE_STORAGE_KEY) || null; } catch { return null; }
  });
  const [exitPromptOpen, setExitPromptOpen] = useState(false);
  const [pinInput, setPinInput] = useState("");

  // Light ☀️ / Dark 🌙 theme — persisted in localStorage so it survives
  // profile switches. Default: light (sunny clouds).
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("tapin_kids_theme") || "light"; } catch { return "light"; }
  });
  useEffect(() => {
    try { localStorage.setItem("tapin_kids_theme", theme); } catch {}
  }, [theme]);
  const toggleTheme = () => setTheme((t) => (t === "light" ? "dark" : "light"));

  // Overlays + sleep timer
  const [searchOpen, setSearchOpen] = useState(false);
  const [learnOpen, setLearnOpen] = useState(false);
  const [sleepMenuOpen, setSleepMenuOpen] = useState(false);
  // Kids Activities overlays (Jr Podcast · Draw · Bedtime · Shorts)
  const [jrStudioOpen, setJrStudioOpen] = useState(false);
  const [drawOpen, setDrawOpen] = useState(false);
  const [bedtimeOpen, setBedtimeOpen] = useState(false);
  const [shortsOpen, setShortsOpen] = useState(false);
  const [sleepRemaining, setSleepRemaining] = useState(null);
  const [sleeping, setSleeping] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const watchTimerRef = useRef(null);

  // Remote-control navigation: arrow keys move between `data-tv-focus`
  // elements; Back/Escape triggers exit flow. Screen wake lock keeps the TV
  // from going to sleep while the Kids app is open.
  useSpatialNav(() => {
    if (exitPromptOpen) setExitPromptOpen(false);
    else if (selectedProfileId && profiles.length > 1) clearProfile();
    else attemptExit();
  });
  useWakeLock(true);

  const currentProfile = profiles.find((p) => p.profile_id === selectedProfileId) || null;
  const avatar = currentProfile?.avatar_emoji || "⭐";
  const avatarImage = currentProfile?.avatar_image_url || null;
  const kidName = currentProfile?.name || (user?.display_name || user?.name || "little star").split(" ")[0];
  const kidHandle = currentProfile?.handle || null;

  useEffect(() => {
    let mounted = true;
    api.get("/tv/kids-home").then((r) => { if (mounted) setHome(r.data || {}); }).catch(() => {});
    if (user?.is_parent) {
      api.get("/kids/profiles").then((r) => {
        if (!mounted) return;
        const list = r.data?.profiles || [];
        setProfiles(list);
        setProfilesLoaded(true);
        // Clear stale selection if the profile was deleted.
        if (selectedProfileId && !list.some((p) => p.profile_id === selectedProfileId)) {
          setSelectedProfileId(null);
          try { localStorage.removeItem(PROFILE_STORAGE_KEY); } catch {}
        }
      }).catch(() => { if (mounted) setProfilesLoaded(true); });
    }
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [user]);

  // Fetch scoped config once a profile is selected (or when no profiles
  // exist — falls back to parent-level default bucket).
  useEffect(() => {
    if (!user?.is_parent || !profilesLoaded) return;
    const q = selectedProfileId ? `?profile_id=${selectedProfileId}` : "";
    api.get(`/kids/config${q}`).then((r) => setCfg(r.data || {})).catch(() => {});
  }, [user, profilesLoaded, selectedProfileId]);

  // Log 30 seconds of watch time every 30s while the kids UI is visible,
  // scoped to the currently-selected profile.
  useEffect(() => {
    if (!user?.is_parent) return;
    // Don't count minutes while the picker is up.
    if (profiles.length > 0 && !selectedProfileId) return;
    watchTimerRef.current = setInterval(async () => {
      try {
        const body = { seconds: 30 };
        if (selectedProfileId) body.profile_id = selectedProfileId;
        const { data } = await api.post("/kids/log-watch", body);
        setCfg((c) => ({ ...c, today_watched_min: data.today_watched_min, remaining_min: data.remaining_min, locked: data.locked }));
      } catch {}
    }, 30_000);
    return () => { if (watchTimerRef.current) clearInterval(watchTimerRef.current); };
  }, [user, selectedProfileId, profiles.length]);

  // Gate: Kids content requires a parent account.
  if (!user) {
    return (
      <div data-testid="kids-login-required" className="min-h-screen bg-gradient-to-br from-[#FFEBA1] via-[#FFB7C5] to-[#C7F0FF] flex items-center justify-center p-6 text-center">
        <div className="bg-white/95 rounded-[2rem] shadow-2xl p-10 max-w-md">
          <div className="text-7xl mb-3">👋</div>
          <h1 className="font-display font-black text-3xl text-[#222] mb-2">Sign in first</h1>
          <p className="text-[#555] mb-6">Kids TV is only available to grown-ups with a parent account.</p>
          <button onClick={() => navigate("/login?next=/tv/kids")} className="w-full bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white font-black text-lg py-3 rounded-full shadow-lg">Sign in</button>
        </div>
      </div>
    );
  }

  if (!user.is_parent) {
    const enableParent = async () => {
      setEnabling(true);
      try {
        await api.post("/parent/enable");
        toast.success("Parent account enabled");
        window.location.reload();
      } catch (e) { toast.error(formatApiError(e)); setEnabling(false); }
    };
    return (
      <div data-testid="kids-parent-required" className="min-h-screen bg-gradient-to-br from-[#FFEBA1] via-[#FFB7C5] to-[#C7F0FF] flex items-center justify-center p-6 text-center">
        <div className="bg-white/95 rounded-[2rem] shadow-2xl p-10 max-w-md">
          <div className="text-7xl mb-3">🛡️</div>
          <h1 className="font-display font-black text-3xl text-[#222] mb-2">Enable your parent account</h1>
          <p className="text-[#555] mb-6">Kids TV is gated to parent accounts. Flip the switch once — you can set a PIN, screen-time caps, and review activity from the Parent Dashboard.</p>
          <button
            data-testid="kids-enable-parent-btn"
            onClick={enableParent}
            disabled={enabling}
            className="w-full bg-gradient-to-r from-fuchsia-500 to-pink-500 disabled:opacity-50 text-white font-black text-lg py-3 rounded-full shadow-lg"
          >{enabling ? "Enabling…" : "Enable parent account"}</button>
          <button onClick={() => navigate("/tv")} className="mt-3 w-full bg-white hover:bg-gray-100 border-2 border-[#ddd] text-[#444] font-bold py-3 rounded-full">Back to Tap IN TV</button>
        </div>
      </div>
    );
  }

  const attemptExit = async () => {
    if (!cfg.pin_set) { clearProfile(); navigate("/"); return; }
    setExitPromptOpen(true);
  };

  const submitPin = async () => {
    try {
      await api.post("/kids/verify-pin", { pin: pinInput });
      toast.success("Unlocked"); setExitPromptOpen(false); setPinInput("");
      clearProfile();
      navigate("/");
    } catch (e) { toast.error(formatApiError(e)); setPinInput(""); }
  };

  const pickProfile = (pid) => {
    setSelectedProfileId(pid);
    try { localStorage.setItem(PROFILE_STORAGE_KEY, pid); } catch {}
  };

  const clearProfile = () => {
    setSelectedProfileId(null);
    try { localStorage.removeItem(PROFILE_STORAGE_KEY); } catch {}
  };

  // Profile picker — shown whenever the parent has profiles defined and
  // none is currently selected. Always rendered in a dreamy starry-moonlit
  // night sky so the "Who's watching?" gateway feels like a bedtime storybook.
  if (profilesLoaded && profiles.length > 0 && !selectedProfileId) {
    return (
      <div
        data-testid="kids-profile-picker"
        className="kids-night fixed inset-0 flex flex-col items-center justify-center p-6 kids-responsive overflow-hidden"
      >
        {/* Deep midnight sky gradient */}
        <div aria-hidden className="pointer-events-none absolute inset-0 z-0" style={{
          background: "radial-gradient(ellipse at 80% 18%, #5c3a9d 0%, transparent 55%), linear-gradient(180deg, #0b1336 0%, #1d1452 35%, #2b1650 60%, #4a1a6a 85%, #6b235e 100%)",
        }} />
        {/* Glowing moon top-right */}
        <div aria-hidden className="pointer-events-none absolute z-0" style={{
          top: "8%", right: "9%",
          width: "clamp(120px, 20vmin, 260px)", height: "clamp(120px, 20vmin, 260px)",
          borderRadius: "50%",
          background: "radial-gradient(circle at 35% 35%, #fff9dc 0%, #fff1b0 45%, #ffd56a 80%, transparent 86%)",
          boxShadow: "0 0 80px 20px rgba(255,231,150,0.5), 0 0 140px 40px rgba(255,231,150,0.28)",
          filter: "drop-shadow(0 0 32px rgba(255,231,150,0.55))",
        }} />
        {/* Dense twinkling starfield */}
        <div aria-hidden className="pointer-events-none absolute inset-0 z-0 kids-stars-dense" />
        <div aria-hidden className="pointer-events-none absolute inset-0 z-0 kids-stars-dense-b" />
        {/* Subtle cloud whisps (dark mode uses lower opacity) */}
        <span aria-hidden className="kids-clouds-night absolute inset-0 z-0 pointer-events-none">
          <span className="c1" /><span className="c2" />
        </span>
        {/* Ground glow so profile row pops */}
        <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[45%]" style={{
          background: "radial-gradient(ellipse at 50% 100%, rgba(180,120,255,0.35), transparent 65%)",
        }} />

        <div className="relative z-10 flex flex-col items-center">
          <img src="/tapintv-kids-logo.png" alt="Tap IN Kids Network" className="h-[14vmin] max-h-36 w-auto mb-[3vmin] drop-shadow-[0_0_24px_rgba(255,231,150,0.45)] animate-[kidsLogoFloat_4s_ease-in-out_infinite]" />
          <h1 className="font-display font-black text-[4.6vmin] md:text-[4vmin] text-white text-center mb-[4vmin] drop-shadow-[0_4px_16px_rgba(0,0,0,0.6)]">
            Who's watching? <span className="text-yellow-200">🌙</span>
          </h1>
          <div className="flex flex-wrap justify-center gap-[3vmin] max-w-[90vw]">
            {profiles.map((p) => (
              <button
                key={p.profile_id}
                data-tv-focus="true"
                data-testid={`kids-profile-${p.profile_id}`}
                onClick={() => pickProfile(p.profile_id)}
                className="tv-focusable kids-focusable group flex flex-col items-center gap-[1.2vmin] focus:outline-none"
              >
                <div className={`w-[18vmin] h-[18vmin] max-w-[180px] max-h-[180px] rounded-[2rem] ${p.avatar_image_url ? "bg-gradient-to-br from-fuchsia-500 via-pink-400 to-amber-300" : "bg-white"} ring-4 ring-white/90 shadow-[0_0_48px_rgba(255,200,255,0.35)] flex items-center justify-center overflow-hidden transition-all group-hover:scale-[1.08] group-focus-visible:scale-[1.08]`}>
                  {p.avatar_image_url
                    ? <img src={p.avatar_image_url} alt={p.name} className="w-full h-full object-cover" />
                    : <span className="text-[10vmin]">{p.avatar_emoji}</span>}
                </div>
                <div className="font-display font-black text-[2.4vmin] md:text-[2vmin] text-white drop-shadow">{p.name}</div>
                <div className="text-[1.6vmin] text-white/70">{p.remaining_min} min left today</div>
                {p.handle && <div className="text-[1.4vmin] text-pink-200 font-bold">@{p.handle.replace(/^@/, "")}</div>}
              </button>
            ))}
          </div>
          <button
            onClick={() => navigate("/parent/dashboard")}
            data-tv-focus="true"
            data-testid="kids-picker-manage"
            className="tv-focusable mt-[6vmin] bg-white/90 hover:bg-white text-[#444] font-bold px-[3vmin] py-[1.2vmin] rounded-full shadow-xl backdrop-blur"
          >Manage profiles</button>
        </div>

        <style>{`
          @keyframes kidsLogoFloat { 0%,100% { transform: translateY(0) rotate(-1deg); } 50% { transform: translateY(-6px) rotate(1deg); } }
          @keyframes twinkle { 0%, 100% { opacity: 0.3; transform: scale(0.85); } 50% { opacity: 1; transform: scale(1.15); } }
          @keyframes driftSlow { from { transform: translateX(-10%); } to { transform: translateX(110%); } }
          @keyframes driftFast { from { transform: translateX(-20%); } to { transform: translateX(120%); } }
          /* Dense starfield — two layered patterns with different twinkle timings */
          .kids-stars-dense {
            background-image:
              radial-gradient(circle, rgba(255,255,255,1) 1.6px, transparent 2.2px),
              radial-gradient(circle, rgba(255,244,210,0.95) 1.2px, transparent 1.8px),
              radial-gradient(circle, rgba(210,230,255,0.9) 1px, transparent 1.6px);
            background-size: 160px 160px, 240px 240px, 340px 340px;
            background-position: 20px 40px, 100px 120px, 240px 200px;
            animation: twinkle 3.6s ease-in-out infinite;
            opacity: 1;
          }
          .kids-stars-dense-b {
            background-image:
              radial-gradient(circle, rgba(255,255,255,0.9) 1.4px, transparent 2px),
              radial-gradient(circle, rgba(255,210,255,0.9) 1px, transparent 1.5px);
            background-size: 200px 200px, 380px 380px;
            background-position: 60px 90px, 280px 140px;
            animation: twinkle 5.2s ease-in-out infinite;
            animation-delay: 1.2s;
            opacity: 0.9;
          }
          .kids-clouds-night::before,
          .kids-clouds-night > span.c1,
          .kids-clouds-night > span.c2 {
            content: ""; position: absolute; left: 0; right: 0;
            height: 100px; background-repeat: repeat-x;
            pointer-events: none; opacity: 0.18;
          }
          .kids-clouds-night::before {
            top: 28%;
            background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 480 120'><g fill='%23d8cdff' opacity='0.88'><ellipse cx='60' cy='70' rx='50' ry='22'/><ellipse cx='110' cy='60' rx='36' ry='26'/><ellipse cx='320' cy='55' rx='46' ry='22'/><ellipse cx='370' cy='65' rx='40' ry='24'/></g></svg>");
            background-size: 540px 100px;
            animation: driftSlow 120s linear infinite;
          }
          .kids-clouds-night > span.c1 {
            top: 62%;
            background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 480 120'><g fill='%23ffd9e8' opacity='0.82'><ellipse cx='70' cy='70' rx='48' ry='22'/><ellipse cx='350' cy='65' rx='56' ry='26'/></g></svg>");
            background-size: 600px 100px;
            animation: driftFast 90s linear infinite reverse;
            opacity: 0.16;
          }
          .kids-clouds-night > span.c2 { display: none; }
          .kids-focusable:focus-visible { outline: 4px solid #ec4899; outline-offset: 6px; }
        `}</style>
      </div>
    );
  }

  // Time-limit lockout screen — shown while the daily cap is reached OR
  // when the optional "time window" is closed.
  if (cfg.locked || !cfg.in_window) {
    return (
      <div data-testid="kids-lockout" className="min-h-screen bg-gradient-to-br from-[#FFEBA1] via-[#FFB7C5] to-[#C7F0FF] flex items-center justify-center p-6 text-center">
        <div className="bg-white/95 rounded-[2rem] shadow-2xl p-10 max-w-md">
          <div className="text-7xl mb-3">🌙</div>
          <h1 className="font-display font-black text-3xl text-[#222] mb-2">{cfg.locked ? "All done for today!" : "Not right now"}</h1>
          <p className="text-[#444] mb-6">
            {cfg.locked
              ? `You watched ${cfg.today_watched_min} minutes today — great job! Come back tomorrow for more fun.`
              : "This is quiet time! Ask a grown-up when you can come back."}
          </p>
          <button
            data-testid="kids-lockout-exit"
            onClick={attemptExit}
            className="w-full bg-gradient-to-r from-fuchsia-500 to-pink-500 hover:from-fuchsia-400 hover:to-pink-400 text-white font-black text-lg py-3 rounded-full shadow-lg"
          >Ask a grown-up</button>
        </div>
        {exitPromptOpen && <PinPrompt pin={pinInput} setPin={setPinInput} onClose={() => setExitPromptOpen(false)} onSubmit={submitPin} />}
      </div>
    );
  }

  const isDark = theme === "dark";
  return (
    <div
      data-testid="kids-app"
      data-theme={theme}
      className={`kids-root fixed inset-0 overflow-hidden flex flex-col ${isDark ? "text-white" : "text-[#222]"}`}
      style={{
        fontSize: "clamp(16px, 1.4vmin, 32px)",
        "--kids-safe-x": "max(24px, 3.2vmin)",
        "--kids-safe-y": "max(16px, 2.4vmin)",
        "--kids-card-w": "clamp(180px, 18vmin, 360px)",
        "--kids-hero-h": "clamp(340px, 56vmin, 1100px)",
        "--kids-gap": "clamp(10px, 1.4vmin, 26px)",
      }}
    >
      {/* Base sky gradient — theme-aware */}
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0" style={{
        background: isDark
          ? "radial-gradient(ellipse at 80% 18%, #5c3a9d 0%, transparent 55%), linear-gradient(180deg, #0b1336 0%, #1d1452 35%, #2b1650 60%, #4a1a6a 85%, #6b235e 100%)"
          : "linear-gradient(180deg, #A6D3FF 0%, #C5B6FF 45%, #FFC4E1 85%, #FFE3B0 100%)",
      }} />
      {/* Moon — only in dark mode */}
      {isDark && (
        <div aria-hidden className="pointer-events-none absolute z-0" style={{
          top: "7%", right: "8%",
          width: "clamp(110px, 18vmin, 240px)", height: "clamp(110px, 18vmin, 240px)",
          borderRadius: "50%",
          background: "radial-gradient(circle at 35% 35%, #fff9dc 0%, #fff1b0 45%, #ffd56a 80%, transparent 86%)",
          boxShadow: "0 0 80px 20px rgba(255,231,150,0.5), 0 0 140px 40px rgba(255,231,150,0.28)",
        }} />
      )}
      {/* Twinkling stars layer — denser in dark mode */}
      <div aria-hidden className={`pointer-events-none absolute inset-0 z-0 ${isDark ? "kids-stars-dense" : "kids-stars"}`} />
      {isDark && <div aria-hidden className="pointer-events-none absolute inset-0 z-0 kids-stars-dense-b" />}
      {/* Soft bottom vignette so cards read on the sky */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[40%]" style={{
        background: isDark
          ? "radial-gradient(ellipse at 50% 100%, rgba(180,120,255,0.35), transparent 65%)"
          : "linear-gradient(to top, rgba(255,246,249,0.55), transparent)",
      }} />

      {/* Header bar — full-width above the hero */}
      <header
        data-testid="kids-header"
        className={`relative z-30 backdrop-blur-md border-b-4 shadow-lg ${isDark ? "bg-black/35 border-white/20" : "bg-white/55 border-white/70"}`}      >
        <div className="px-[2vmin] py-[1.2vmin] flex items-center gap-[1.6vmin]">
          <img
            src="/tapintv-kids-logo.png"
            alt="Tap IN Kids Network"
            data-testid="kids-brand-logo"
            className="h-[7vmin] max-h-20 min-h-[44px] w-auto drop-shadow-[0_4px_12px_rgba(236,72,153,0.35)] shrink-0 select-none"
            draggable={false}
          />
          <div data-testid="kids-avatar" className="ml-1 shrink-0">
            {avatarImage
              ? <img src={avatarImage} alt={kidName} className="w-[6vmin] h-[6vmin] min-w-[40px] min-h-[40px] rounded-2xl object-cover ring-2 ring-white shadow" />
              : <span className="text-[4vmin]">{avatar}</span>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-[2.4vmin] md:text-[2.1vmin] truncate">Hi, {kidName}! 👋</div>
            {kidHandle && <div className="text-[1.2vmin] font-black text-fuchsia-600 uppercase tracking-wider">@{kidHandle.replace(/^@/, "")}</div>}
            <TimeRing remaining={cfg.remaining_min} limit={cfg.daily_limit_min || 60} />
          </div>
          <a
            href="/parent/dashboard"
            data-tv-focus="true"
            data-testid="kids-parent-link"
            title="Parent dashboard"
            className="tv-focusable kids-focusable w-[5vmin] h-[5vmin] min-w-[40px] min-h-[40px] rounded-full bg-white/85 hover:bg-white flex items-center justify-center shadow text-[#222]"
          ><Settings className="w-[2.4vmin] h-[2.4vmin] min-w-[18px]" /></a>
          {profiles.length > 1 && (
            <button
              data-tv-focus="true"
              data-testid="kids-switch-profile-btn"
              onClick={clearProfile}
              title="Switch kid"
              className="tv-focusable kids-focusable w-[5vmin] h-[5vmin] min-w-[40px] min-h-[40px] rounded-full bg-white/85 hover:bg-white flex items-center justify-center shadow text-[#222]"
            ><Users className="w-[2.4vmin] h-[2.4vmin] min-w-[18px]" /></button>
          )}
          <button
            data-tv-focus="true"
            data-testid="kids-search-btn"
            onClick={() => setSearchOpen(true)}
            title="Search"
            className="tv-focusable kids-focusable w-[5vmin] h-[5vmin] min-w-[40px] min-h-[40px] rounded-full bg-white/85 hover:bg-white text-[#222] flex items-center justify-center shadow"
          ><Search className="w-[2.4vmin] h-[2.4vmin] min-w-[18px]" /></button>
          <button
            data-tv-focus="true"
            data-testid="kids-learn-btn"
            onClick={() => setLearnOpen(true)}
            title="Learn &amp; Play"
            className="tv-focusable kids-focusable w-[5vmin] h-[5vmin] min-w-[40px] min-h-[40px] rounded-full bg-amber-300 hover:bg-amber-200 text-[#222] flex items-center justify-center shadow"
          ><BookOpen className="w-[2.4vmin] h-[2.4vmin] min-w-[18px]" /></button>
          <button
            data-tv-focus="true"
            data-testid="kids-jr-studio-btn"
            onClick={() => setJrStudioOpen(true)}
            title="Jr Podcast Studio"
            className="tv-focusable kids-focusable w-[5vmin] h-[5vmin] min-w-[40px] min-h-[40px] rounded-full bg-pink-400 hover:bg-pink-300 text-white flex items-center justify-center shadow"
          ><Radio className="w-[2.4vmin] h-[2.4vmin] min-w-[18px]" /></button>
          <button
            data-tv-focus="true"
            data-testid="kids-draw-btn"
            onClick={() => setDrawOpen(true)}
            title="Draw &amp; share"
            className="tv-focusable kids-focusable w-[5vmin] h-[5vmin] min-w-[40px] min-h-[40px] rounded-full bg-fuchsia-400 hover:bg-fuchsia-300 text-white flex items-center justify-center shadow"
          ><Palette className="w-[2.4vmin] h-[2.4vmin] min-w-[18px]" /></button>
          <button
            data-tv-focus="true"
            data-testid="kids-bedtime-btn"
            onClick={() => setBedtimeOpen(true)}
            title="Bedtime lullabies"
            className="tv-focusable kids-focusable w-[5vmin] h-[5vmin] min-w-[40px] min-h-[40px] rounded-full bg-indigo-400 hover:bg-indigo-300 text-white flex items-center justify-center shadow"
          ><BedDouble className="w-[2.4vmin] h-[2.4vmin] min-w-[18px]" /></button>
          <button
            data-tv-focus="true"
            data-testid="kids-shorts-btn"
            onClick={() => setShortsOpen(true)}
            title="Kids Shorts"
            className="tv-focusable kids-focusable w-[5vmin] h-[5vmin] min-w-[40px] min-h-[40px] rounded-full bg-cyan-400 hover:bg-cyan-300 text-white flex items-center justify-center shadow"
          ><Film className="w-[2.4vmin] h-[2.4vmin] min-w-[18px]" /></button>
          <button
            data-tv-focus="true"
            data-testid="kids-sleep-btn"
            onClick={() => setSleepMenuOpen((v) => !v)}
            title="Sleep timer"
            className={`tv-focusable kids-focusable relative w-[5vmin] h-[5vmin] min-w-[40px] min-h-[40px] rounded-full flex items-center justify-center shadow ${sleepRemaining != null ? "bg-indigo-500 text-white animate-pulse" : "bg-indigo-900/70 hover:bg-indigo-800 text-white"}`}
          >
            <Moon className="w-[2.4vmin] h-[2.4vmin] min-w-[18px]" />
            {sleepRemaining != null && <span className="absolute -top-1 -right-1 bg-yellow-300 text-[#222] text-[10px] font-black min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center">{sleepRemaining}</span>}
          </button>
          <button
            data-tv-focus="true"
            data-testid="kids-theme-toggle"
            onClick={toggleTheme}
            title={isDark ? "Switch to light mode" : "Switch to night mode"}
            className={`tv-focusable kids-focusable w-[5vmin] h-[5vmin] min-w-[40px] min-h-[40px] rounded-full flex items-center justify-center shadow ${
              isDark ? "bg-yellow-300/90 hover:bg-yellow-200 text-[#222]" : "bg-indigo-600/90 hover:bg-indigo-500 text-white"
            }`}
          >
            {isDark
              ? <Sun className="w-[2.4vmin] h-[2.4vmin] min-w-[18px]" />
              : <Moon className="w-[2.4vmin] h-[2.4vmin] min-w-[18px]" />}
          </button>
          <button
            data-tv-focus="true"
            data-testid="kids-exit-btn"
            onClick={attemptExit}
            className="tv-focusable kids-focusable inline-flex items-center gap-1.5 bg-gradient-to-r from-fuchsia-500 to-pink-500 hover:from-fuchsia-400 hover:to-pink-400 text-white font-black text-[1.6vmin] min-text-sm px-[1.6vmin] py-[1vmin] rounded-full shadow-lg"
          ><LogOut className="w-[2vmin] h-[2vmin] min-w-[16px]" /> Exit</button>
        </div>
      </header>

      {/* Scrollable body */}
      <main
        className="kids-body flex-1 overflow-y-auto overflow-x-hidden relative z-10"
        style={{ scrollPaddingTop: "calc(var(--kids-safe-y) + 2em)", scrollBehavior: "smooth" }}
      >
        {/* Full-bleed Netflix-style hero at the top */}
        {home.hero?.length > 0 && <KidsHero items={home.hero} />}

        {/* Rails & content below hero */}
        <div className="flex flex-col" style={{ rowGap: "calc(var(--kids-gap)*2)", marginTop: "calc(var(--kids-gap)*0.8)", paddingBottom: "var(--kids-safe-y)" }}>
          {/* Welcome brand banner (compact version under hero) */}
          {home.hero?.length === 0 && (
            <section data-testid="kids-brand-banner" className="relative rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white bg-gradient-to-r from-[#6a8dff] via-[#b18dff] to-[#ff7fb6] mx-[var(--kids-safe-x)] p-[2.4vmin]">
              <div className="absolute inset-0 pointer-events-none opacity-[0.28]" style={{
                backgroundImage: "radial-gradient(circle at 20% 30%, #fff 2px, transparent 2.5px), radial-gradient(circle at 80% 60%, #fff 1.5px, transparent 2px), radial-gradient(circle at 50% 80%, #fff 1.5px, transparent 2px)",
                backgroundSize: "120px 120px, 90px 90px, 150px 150px",
              }} />
              <div className="relative flex items-center gap-[2vmin]">
                <img src="/tapintv-kids-logo.png" alt="" className="w-[14vmin] max-w-40 h-auto drop-shadow-[0_8px_24px_rgba(0,0,0,0.35)] shrink-0 select-none animate-[kidsLogoFloat_4s_ease-in-out_infinite]" draggable={false} />
                <div className="text-white flex-1 min-w-0">
                  <div className="text-[1.4vmin] uppercase tracking-[0.28em] font-black text-yellow-200 drop-shadow">KIDS · SAFE · AD-LIGHT</div>
                  <h1 className="font-display font-black text-[3.2vmin] md:text-[2.8vmin] leading-tight drop-shadow">Welcome to your own world!</h1>
                  <p className="text-white/90 text-[1.5vmin] mt-1">Hand-picked by grown-ups. Only on the Tap IN Kids Network.</p>
                </div>
              </div>
            </section>
          )}

          <KidsCategories items={home.rails || []} />

          <KidsFunZone
            onJrStudio={() => setJrStudioOpen(true)}
            onDraw={() => setDrawOpen(true)}
            onBedtime={() => setBedtimeOpen(true)}
            onShorts={() => setShortsOpen(true)}
          />

          {home.live_now?.length > 0 && (
            <KidsRail title="Watch together — LIVE now! 🎉" items={home.live_now} />
          )}
          {home.coming_soon?.length > 0 && (
            <KidsComingSoon items={home.coming_soon} />
          )}
          {home.rails?.length === 0 && home.hero?.length === 0 && (
            <div className="text-center py-[10vmin]">
              <div className="text-[10vmin] mb-2">🎨</div>
              <h2 className="font-display font-black text-[3.6vmin]">Stories coming soon!</h2>
              <p className="text-[#666] text-[2vmin] mt-1">Grown-ups are setting up fun things for you to watch.</p>
            </div>
          )}
          {(home.rails || []).map((r) => (
            <KidsRail key={r.id} id={r.id} title={r.title} subtitle={r.subtitle} items={r.videos || []} />
          ))}

          <KidsFooter />
        </div>
      </main>

      <style>{`
        /* Responsive TV-scale variables already set in inline style above.
           Kids-specific focus ring + playful transitions below. */
        @keyframes kidsLogoFloat { 0%,100% { transform: translateY(0) rotate(-1deg); } 50% { transform: translateY(-6px) rotate(1deg); } }
        @keyframes kidsBounceIn { 0% { transform: scale(0.85); opacity: 0; } 60% { transform: scale(1.05); opacity: 1; } 100% { transform: scale(1); } }

        /* Theme-aware heading & muted text */
        .kids-root[data-theme="light"] .kids-h { color: #222; }
        .kids-root[data-theme="dark"]  .kids-h { color: #fff; text-shadow: 0 2px 8px rgba(0,0,0,0.45); }
        .kids-root[data-theme="light"] .kids-muted { color: #555; }
        .kids-root[data-theme="dark"]  .kids-muted { color: rgba(255,255,255,0.72); }

        /* ---- Sky background: twinkling stars + drifting clouds ---- */
        @keyframes twinkle {
          0%, 100% { opacity: 0.25; transform: scale(0.85); }
          50% { opacity: 1; transform: scale(1.1); }
        }
        @keyframes driftSlow { from { transform: translateX(-10%); } to { transform: translateX(110%); } }
        @keyframes driftFast { from { transform: translateX(-20%); } to { transform: translateX(120%); } }

        /* Light-mode stars — soft, few */
        .kids-stars {
          background-image:
            radial-gradient(circle, rgba(255,255,255,0.95) 1px, transparent 1.6px),
            radial-gradient(circle, rgba(255,244,210,0.9)  1px, transparent 1.6px),
            radial-gradient(circle, rgba(255,255,255,0.8)  1.4px, transparent 2px);
          background-size: 220px 220px, 320px 320px, 460px 460px;
          background-position: 20px 40px, 100px 120px, 240px 200px;
          animation: twinkle 4.5s ease-in-out infinite;
          opacity: 0.85;
        }
        /* Dark-mode stars — dense, bright */
        .kids-stars-dense {
          background-image:
            radial-gradient(circle, rgba(255,255,255,1) 1.6px, transparent 2.2px),
            radial-gradient(circle, rgba(255,244,210,0.95) 1.2px, transparent 1.8px),
            radial-gradient(circle, rgba(210,230,255,0.9) 1px, transparent 1.6px);
          background-size: 160px 160px, 240px 240px, 340px 340px;
          background-position: 20px 40px, 100px 120px, 240px 200px;
          animation: twinkle 3.6s ease-in-out infinite;
          opacity: 1;
        }
        .kids-stars-dense-b {
          background-image:
            radial-gradient(circle, rgba(255,255,255,0.9) 1.4px, transparent 2px),
            radial-gradient(circle, rgba(255,210,255,0.9) 1px, transparent 1.5px);
          background-size: 200px 200px, 380px 380px;
          background-position: 60px 90px, 280px 140px;
          animation: twinkle 5.2s ease-in-out infinite;
          animation-delay: 1.2s;
          opacity: 0.9;
        }

        /* Clouds — light mode only */
        .kids-root[data-theme="light"] .kids-clouds::before,
        .kids-root[data-theme="light"] .kids-clouds::after,
        .kids-root[data-theme="light"] .kids-clouds > span.c1,
        .kids-root[data-theme="light"] .kids-clouds > span.c2 {
          content: ""; position: absolute; left: 0; right: 0;
          height: 120px;
          background-repeat: repeat-x;
          background-size: 480px 120px;
          opacity: 0.9;
          pointer-events: none;
        }
        .kids-root[data-theme="light"] .kids-clouds::before {
          top: 8%;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 480 120'><g fill='%23ffffff' opacity='0.95'><ellipse cx='60' cy='70' rx='50' ry='22'/><ellipse cx='110' cy='60' rx='36' ry='26'/><ellipse cx='150' cy='70' rx='30' ry='20'/><ellipse cx='320' cy='55' rx='46' ry='22'/><ellipse cx='370' cy='65' rx='40' ry='24'/><ellipse cx='410' cy='70' rx='28' ry='18'/></g></svg>");
          animation: driftSlow 90s linear infinite;
        }
        .kids-root[data-theme="light"] .kids-clouds::after {
          top: 36%;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 480 120'><g fill='%23ffffff' opacity='0.88'><ellipse cx='90' cy='60' rx='60' ry='24'/><ellipse cx='150' cy='55' rx='30' ry='20'/><ellipse cx='280' cy='70' rx='50' ry='22'/><ellipse cx='340' cy='60' rx='34' ry='24'/></g></svg>");
          background-size: 560px 120px;
          animation: driftFast 70s linear infinite reverse;
          opacity: 0.78;
        }
        .kids-root[data-theme="light"] .kids-clouds > span.c1 {
          top: 58%;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 480 120'><g fill='%23ffffff' opacity='0.78'><ellipse cx='70' cy='70' rx='48' ry='22'/><ellipse cx='130' cy='60' rx='32' ry='22'/><ellipse cx='350' cy='65' rx='56' ry='26'/></g></svg>");
          background-size: 520px 120px;
          animation: driftSlow 110s linear infinite;
          opacity: 0.72;
        }
        .kids-root[data-theme="light"] .kids-clouds > span.c2 {
          top: 78%;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 480 120'><g fill='%23ffffff' opacity='0.7'><ellipse cx='100' cy='60' rx='54' ry='22'/><ellipse cx='300' cy='70' rx='44' ry='20'/><ellipse cx='360' cy='60' rx='32' ry='22'/></g></svg>");
          animation: driftFast 95s linear infinite reverse;
          opacity: 0.6;
        }
        /* In dark mode, suppress the daytime cloud layer (stars/moon take over) */
        .kids-root[data-theme="dark"] .kids-clouds::before,
        .kids-root[data-theme="dark"] .kids-clouds::after,
        .kids-root[data-theme="dark"] .kids-clouds > span.c1,
        .kids-root[data-theme="dark"] .kids-clouds > span.c2 { display: none; }
        .kids-clouds { overflow: hidden; }

        .kids-tile { animation: kidsBounceIn 350ms ease-out both; }
        .kids-tile:hover, .kids-tile:focus-visible { transform: scale(1.08) rotate(-1deg); transition: transform 200ms ease-out; }

        /* Shared TV focus ring — bright pink so kids can see where the remote is */
        .tv-focusable:focus-visible,
        .kids-focusable:focus-visible {
          outline: 4px solid #ec4899;
          outline-offset: 6px;
          box-shadow: 0 0 0 8px rgba(236,72,153,0.22), 0 16px 36px -6px rgba(0,0,0,0.35);
          transition: outline-offset 120ms ease, box-shadow 160ms ease, transform 180ms ease;
        }

        /* Body + rail scroll with hidden scrollbars (Netflix/kids-TV friendly) */
        .kids-body { scrollbar-width: none; -webkit-overflow-scrolling: touch; }
        .kids-body::-webkit-scrollbar { display: none; width: 0; height: 0; }
        .kids-rail { scrollbar-width: none; -webkit-overflow-scrolling: touch; scroll-snap-type: x proximity; }
        .kids-rail::-webkit-scrollbar { display: none; width: 0; height: 0; }
        .kids-rail > * { scroll-snap-align: start; }

        /* Netflix-style card hover scale (bigger + pinker for kids) */
        .kids-card-scale { transform-origin: left center; transition: transform 260ms cubic-bezier(.2,.8,.2,1), box-shadow 220ms ease; will-change: transform; }
        .kids-card-scale:hover, .kids-card-scale:focus-visible { transform: scale(1.12) translateY(-6px); z-index: 20; box-shadow: 0 22px 46px -8px rgba(236,72,153,0.45); }
        .kids-rail > .kids-card-scale:last-child { transform-origin: right center; }
      `}</style>
      {/* extra cloud bands — CSS-only markers for ::before / ::after siblings above */}
      <span aria-hidden className="kids-clouds absolute inset-0 z-0 pointer-events-none">
        <span className="c1" />
        <span className="c2" />
      </span>
      {exitPromptOpen && <PinPrompt pin={pinInput} setPin={setPinInput} onClose={() => setExitPromptOpen(false)} onSubmit={submitPin} />}

      {searchOpen && <KidsSearch onClose={() => setSearchOpen(false)} onPlay={(v) => navigate(`/kids/watch/${v.id}`)} />}
      {learnOpen && <KidsFlashcards onClose={() => setLearnOpen(false)} />}
      {jrStudioOpen && <JrPodcastStudio onClose={() => setJrStudioOpen(false)} />}
      {drawOpen && <KidsDraw onClose={() => setDrawOpen(false)} />}
      {bedtimeOpen && <KidsBedtime onClose={() => setBedtimeOpen(false)} />}
      {shortsOpen && <KidsShorts onClose={() => setShortsOpen(false)} />}
      {sleepMenuOpen && (
        <KidsSleepTimer
          remaining={sleepRemaining}
          setRemaining={setSleepRemaining}
          onClose={() => setSleepMenuOpen(false)}
          onFire={() => { setSleepRemaining(null); setSleeping(true); setSleepMenuOpen(false); }}
        />
      )}
      {sleeping && (
        <div data-testid="kids-bedtime-lock" className="fixed inset-0 z-[60] flex flex-col items-center justify-center text-white overflow-hidden" style={{ background: "radial-gradient(ellipse at 50% 0%, #3a1a7a 0%, #0b1336 60%, #050914 100%)" }}>
          <div aria-hidden className="pointer-events-none absolute inset-0 kids-stars-dense" />
          <div className="relative z-10 text-center">
            <div className="text-[16vmin] mb-[2vmin]">🌙</div>
            <div className="font-display font-black text-[5vmin] mb-[1vmin]">Bedtime 💤</div>
            <div className="text-white/75 text-[2.2vmin] mb-[3vmin]">Sweet dreams! Ask a grown-up to unlock.</div>
            <button data-tv-focus="true" data-testid="kids-bedtime-exit" onClick={() => { setSleeping(false); attemptExit(); }}
              className="tv-focusable kids-focusable bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-black px-[3vmin] py-[1.4vmin] rounded-full text-[2vmin] shadow-xl">Grown-up: exit</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Kids-exclusive visual components
// ------------------------------------------------------------------

/** Circular progress ring showing time remaining today. */
function TimeRing({ remaining, limit }) {
  const pct = Math.max(0, Math.min(1, (remaining ?? 0) / Math.max(1, limit || 60)));
  const size = 22;
  const r = 9;
  const c = 2 * Math.PI * r;
  const stroke = Math.max(0, c * pct);
  return (
    <div className="text-[#666] text-xs flex items-center gap-2" data-testid="kids-time-ring">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden className="-ml-0.5">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(0,0,0,0.1)" strokeWidth="3" />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={pct > 0.3 ? "#10b981" : pct > 0.1 ? "#f59e0b" : "#ef4444"}
          strokeWidth="3" strokeLinecap="round"
          strokeDasharray={`${stroke} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <span><b>{remaining ?? 0}</b> min of <b>{limit || 60}</b> left today</span>
    </div>
  );
}

/** Colourful kid-safe category tiles — always visible so navigation feels
 *  natural even before rails load. Maps rail titles to fun tile colours and
 *  emoji. Unmatched titles fall back to a default friendly tile. */
function KidsCategories({ items }) {
  const PALETTE = [
    { bg: "from-[#ff7a7a] to-[#ff3b3b]", emoji: "🎨", label: "Cartoons" },
    { bg: "from-[#ffcd3a] to-[#ff8a00]", emoji: "🎵", label: "Sing-along" },
    { bg: "from-[#4ecdc4] to-[#13a8a1]", emoji: "📚", label: "Storytime" },
    { bg: "from-[#9b6dff] to-[#5a32b5]", emoji: "🧠", label: "Learn" },
    { bg: "from-[#ff7fb6] to-[#e64398]", emoji: "🕹️", label: "Games" },
    { bg: "from-[#64c7ff] to-[#2f7dd1]", emoji: "🎬", label: "Movies" },
  ];
  const tiles = (items || []).slice(0, 6).map((r, i) => ({
    bg: PALETTE[i % PALETTE.length].bg,
    emoji: r.emoji || PALETTE[i % PALETTE.length].emoji,
    label: r.title.replace(/!|🎉|⭐/g, "").slice(0, 18),
    anchor: `#rail-${r.id}`,
  }));
  const display = tiles.length > 0 ? tiles : PALETTE.slice(0, 6).map((p) => ({ ...p, anchor: "#" }));
  return (
    <section data-testid="kids-categories" aria-label="Kids categories" className="mx-[var(--kids-safe-x)]">
      <h2 className="kids-h font-display font-black text-[2.6vmin] mb-[1.2vmin]">Pick a world 🌈</h2>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-[1.2vmin]">
        {display.map((c, i) => (
          <a
            key={i}
            href={c.anchor}
            data-tv-focus="true"
            data-testid={`kids-cat-${i}`}
            className={`kids-tile kids-focusable tv-focusable relative rounded-[1.5rem] aspect-[4/3] bg-gradient-to-br ${c.bg} ring-4 ring-white shadow-lg flex flex-col items-center justify-center gap-1 text-white overflow-hidden`}
            style={{ animationDelay: `${i * 80}ms` }}
          >
            <div className="text-[4.5vmin] drop-shadow-[0_3px_0_rgba(0,0,0,0.15)]">{c.emoji}</div>
            <div className="font-display font-black text-[1.8vmin] drop-shadow text-center px-1 truncate max-w-full">{c.label}</div>
          </a>
        ))}
      </div>
    </section>
  );
}

function KidsFooter() {
  return (
    <footer data-testid="kids-footer" className="pt-[2vmin] border-t-4 border-white/50 mt-[2vmin] mx-[var(--kids-safe-x)]">
      <div className="flex flex-col md:flex-row items-center justify-center gap-[1.6vmin] text-center">
        <img src="/tapintv-kids-logo.png" alt="Tap IN Kids Network" className="h-[8vmin] max-h-20 min-h-[40px] w-auto drop-shadow" />
        <div className="kids-muted text-[1.5vmin] max-w-md">
          <div className="kids-h font-display font-black text-[1.8vmin]">Tap IN Kids Network</div>
          Reviewed by real grown-ups. No chats, no purchases, no creepy comments — just fun.
        </div>
      </div>
    </footer>
  );
}

function KidsHero({ items }) {
  const [i, setI] = useState(0);
  const [imgOk, setImgOk] = useState(true);
  useEffect(() => {
    if ((items || []).length <= 1) return;
    const t = setInterval(() => setI((x) => (x + 1) % items.length), 7000);
    return () => clearInterval(t);
  }, [items]);
  useEffect(() => { setImgOk(true); }, [i]);

  const item = items[i];
  if (!item) return null;

  // Kid-friendly pastel palette — always visible fallback
  const GRADS = [
    "from-[#6a8dff] via-[#b18dff] to-[#ff7fb6]",
    "from-[#ffcd3a] via-[#ff7fb6] to-[#b18dff]",
    "from-[#4ecdc4] via-[#64c7ff] to-[#6a8dff]",
    "from-[#ff9a3c] via-[#ff7fb6] to-[#c06bff]",
  ];
  const grad = GRADS[i % GRADS.length];

  return (
    <div
      data-testid={`kids-hero-${item.id}`}
      className="relative w-full overflow-hidden"
      style={{ height: "var(--kids-hero-h)" }}
    >
      {/* Gradient fallback base — always visible */}
      <div className={`absolute inset-0 bg-gradient-to-br ${grad}`} />
      {/* Kid artwork fills the frame */}
      {item.image_url && imgOk && (
        <img src={item.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" onError={() => setImgOk(false)} />
      )}
      {/* Sparkles + official logo watermark when no artwork */}
      {(!item.image_url || !imgOk) && (
        <>
          <div className="absolute inset-0 pointer-events-none opacity-[0.3]" style={{
            backgroundImage: "radial-gradient(circle at 20% 30%, #fff 2px, transparent 2.5px), radial-gradient(circle at 80% 60%, #fff 1.5px, transparent 2px), radial-gradient(circle at 50% 80%, #fff 1.5px, transparent 2px)",
            backgroundSize: "120px 120px, 90px 90px, 150px 150px",
          }} />
          <img src="/tapintv-kids-logo.png" alt="" aria-hidden className="absolute right-[5%] top-1/2 -translate-y-1/2 opacity-70 animate-[kidsLogoFloat_4s_ease-in-out_infinite] pointer-events-none select-none" style={{ width: "42vmin", maxWidth: 680 }} draggable={false} />
        </>
      )}
      {/* Left-to-right darkening ramp for text legibility — soft for kids */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/25 to-transparent" />
      {/* Bottom fade into page */}
      <div className="absolute inset-x-0 bottom-0 h-[40%] bg-gradient-to-t from-[#FFF6F9] via-[#FFF6F9]/80 to-transparent" />

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-end" style={{ padding: "0 var(--kids-safe-x) calc(var(--kids-safe-y) * 1.8)", maxWidth: "min(820px, 58vw)" }}>
        <div className="text-white/90 uppercase tracking-[0.22em] font-black text-[1.4vmin] mb-[1.2vmin] inline-flex items-center gap-2 drop-shadow">
          <span className="inline-block w-[1.2vmin] h-[1.2vmin] rounded-full bg-yellow-300" />
          KIDS · FEATURED
        </div>
        <h1 className="font-display font-black text-[5vmin] md:text-[4.2vmin] text-white leading-[1.05] drop-shadow-2xl">{item.title}</h1>
        {item.subtitle && <div className="text-white/90 text-[2vmin] mt-2 drop-shadow max-w-[55ch] line-clamp-2">{item.subtitle}</div>}
        <div className="flex items-center gap-[1.2vmin] flex-wrap mt-[2vmin]">
          <a
            href={item.watch_url || "#"}
            data-tv-focus="true"
            data-testid="kids-hero-play"
            className="kids-focusable tv-focusable inline-flex items-center gap-[1vmin] bg-yellow-300 hover:bg-yellow-200 text-[#222] font-black text-[2.2vmin] px-[2.4vmin] py-[1.2vmin] rounded-full shadow-xl"
          ><Play className="w-[2.4vmin] h-[2.4vmin] min-w-[18px] fill-[#222]" /> Play</a>
          {item.info_url && (
            <a
              href={item.info_url}
              data-tv-focus="true"
              className="kids-focusable tv-focusable inline-flex items-center gap-[1vmin] bg-white/90 hover:bg-white text-[#222] font-bold text-[1.8vmin] px-[2vmin] py-[1.2vmin] rounded-full shadow"
            >More Info</a>
          )}
        </div>
      </div>

      {/* Slide dots */}
      {items.length > 1 && (
        <div className="absolute bottom-[1.6vmin] right-[var(--kids-safe-x)] flex items-center gap-[0.8vmin] z-10">
          {items.map((_, n) => (
            <button
              key={n}
              data-tv-focus="true"
              aria-label={`Slide ${n + 1}`}
              data-testid={`kids-hero-dot-${n}`}
              onClick={() => setI(n)}
              className="kids-focusable tv-focusable h-[0.8vmin] min-h-[5px] rounded-full transition-all"
              style={{ width: n === i ? "4vmin" : "1.4vmin", background: n === i ? "#fff" : "rgba(255,255,255,0.45)" }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function KidsRail({ title, subtitle, items, id }) {
  if (!items || items.length === 0) return null;
  return (
    <section data-testid="kids-rail" id={id ? `rail-${id}` : undefined} className="scroll-mt-24 mx-[var(--kids-safe-x)]">
      <h2 className="kids-h font-display font-black text-[2.6vmin] mb-[0.8vmin]">{title}</h2>
      {subtitle && <div className="kids-muted text-[1.6vmin] mb-[1vmin]">{subtitle}</div>}
      <div className="kids-rail flex gap-[1.6vmin] overflow-x-auto pb-[1.6vmin] pt-[1vmin] -mx-[1vmin] px-[1vmin]">
        {items.map((v, i) => (
          <a
            key={v.id}
            href={v.kind === "live" ? `/kids/live/${v.id}` : `/kids/watch/${v.id}`}
            data-tv-focus="true"
            data-testid={`kids-item-${v.id}`}
            className="kids-tile kids-card-scale kids-focusable tv-focusable shrink-0 group"
            style={{ width: "var(--kids-card-w)", animationDelay: `${Math.min(i, 8) * 50}ms` }}
          >
            <div className="aspect-video bg-black rounded-[1.5rem] overflow-hidden ring-4 ring-white shadow-lg">
              {v.thumbnail_url
                ? <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full bg-gradient-to-br from-fuchsia-400 to-sky-400 flex items-center justify-center text-[5vmin]">🎬</div>}
            </div>
            <div className="kids-h mt-[0.8vmin] font-bold text-[1.8vmin] truncate">{v.title}</div>
          </a>
        ))}
      </div>
    </section>
  );
}

/** Fun Zone — big colorful tiles for the 4 Kids Activities.
 *  Rendered prominently between KidsCategories and the content rails so
 *  kids can find Jr Podcast / Draw / Bedtime / Shorts without hunting. */
function KidsFunZone({ onJrStudio, onDraw, onBedtime, onShorts }) {
  const TILES = [
    { key: "jr",  label: "Jr Podcast", sub: "Go live with friends", emoji: "🎙️", bg: "from-pink-400 via-fuchsia-500 to-purple-600",   onClick: onJrStudio, testid: "funzone-jr-studio" },
    { key: "dr",  label: "Draw & Share", sub: "Make art for grown-ups", emoji: "🎨", bg: "from-orange-400 via-pink-500 to-rose-500",     onClick: onDraw,     testid: "funzone-draw" },
    { key: "bt",  label: "Bedtime",    sub: "Lullabies to sleep to",  emoji: "🌙", bg: "from-indigo-500 via-violet-600 to-slate-800",   onClick: onBedtime,  testid: "funzone-bedtime" },
    { key: "sh",  label: "Kid Shorts", sub: "Tiny fun videos",        emoji: "📺", bg: "from-cyan-400 via-sky-500 to-blue-700",         onClick: onShorts,   testid: "funzone-shorts" },
  ];
  return (
    <section data-testid="kids-fun-zone" className="mx-[var(--kids-safe-x)]">
      <h2 className="kids-h font-display font-black text-[2.6vmin] mb-[1vmin] inline-flex items-center gap-[1vmin]">
        <Sparkles className="w-[2.8vmin] h-[2.8vmin] min-w-[20px] text-pink-500" />
        Fun Zone
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-[1.6vmin]">
        {TILES.map((t, i) => (
          <button
            key={t.key}
            data-tv-focus="true"
            data-testid={t.testid}
            onClick={t.onClick}
            style={{ animationDelay: `${i * 60}ms` }}
            className={`kids-tile tv-focusable kids-focusable relative overflow-hidden rounded-[2rem] aspect-[4/3] ring-4 ring-white shadow-xl bg-gradient-to-br ${t.bg} text-white text-left p-[2vmin] flex flex-col justify-between`}
          >
            <div aria-hidden className="absolute -top-6 -right-6 text-[12vmin] opacity-30 pointer-events-none select-none">{t.emoji}</div>
            <div className="relative z-10 text-[7vmin] drop-shadow-lg leading-none">{t.emoji}</div>
            <div className="relative z-10">
              <div className="font-display font-black text-[2.4vmin] leading-tight drop-shadow">{t.label}</div>
              <div className="text-white/85 text-[1.5vmin] font-bold">{t.sub}</div>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}



function KidsComingSoon({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <section data-testid="kids-coming-soon" className="mx-[var(--kids-safe-x)]">
      <h2 className="kids-h font-display font-black text-[2.6vmin] mb-[0.8vmin]">Coming soon! 🎁</h2>
      <div className="kids-rail flex gap-[1.6vmin] overflow-x-auto pb-[1.6vmin] pt-[1vmin] -mx-[1vmin] px-[1vmin]">
        {items.map((cs, i) => {
          const ms = new Date(cs.release_at).getTime() - Date.now();
          const days = Math.max(0, Math.floor(ms / 86_400_000));
          const hours = Math.max(0, Math.floor((ms % 86_400_000) / 3_600_000));
          return (
            <div key={cs.id} data-tv-focus="true" className="kids-tile kids-card-scale kids-focusable tv-focusable shrink-0" style={{ width: "calc(var(--kids-card-w) * 0.75)", animationDelay: `${Math.min(i, 8) * 60}ms` }}>
              <div className="aspect-[2/3] bg-black rounded-[1.5rem] overflow-hidden ring-4 ring-white shadow-lg">
                {cs.poster_url
                  ? <img src={cs.poster_url} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full bg-gradient-to-br from-amber-300 to-orange-400 flex items-center justify-center text-[7vmin]">⭐</div>}
              </div>
              <div className="kids-h mt-[0.8vmin] font-bold text-[1.8vmin] truncate">{cs.title}</div>
              <div className="text-[1.6vmin] text-pink-300 font-black">
                {ms <= 0 ? "Out now!" : days > 0 ? `In ${days} days` : `In ${hours} hours`}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function PinPrompt({ pin, setPin, onClose, onSubmit }) {
  return (
    <div data-testid="kids-pin-prompt" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-[2rem] shadow-2xl p-8 max-w-sm w-full text-center">
        <div className="text-5xl mb-3">🔒</div>
        <h2 className="font-display font-black text-2xl text-[#222] mb-1">Grown-up check</h2>
        <p className="text-[#666] text-sm mb-4">Ask a grown-up to type their PIN.</p>
        <input
          data-testid="kids-pin-input"
          type="password"
          inputMode="numeric"
          autoFocus
          maxLength={6}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => { if (e.key === "Enter") onSubmit(); }}
          placeholder="••••"
          className="w-full bg-[#FFF6F9] border-4 border-[#FFB7C5] rounded-2xl px-4 py-3 text-center text-2xl font-black tracking-[0.5em] text-[#222] outline-none focus:border-fuchsia-500"
        />
        <div className="flex gap-2 mt-4">
          <button
            onClick={onClose}
            data-testid="kids-pin-cancel"
            className="flex-1 bg-white hover:bg-gray-100 border-2 border-[#ddd] text-[#444] font-bold py-3 rounded-full"
          >Stay here</button>
          <button
            onClick={onSubmit}
            data-testid="kids-pin-submit"
            disabled={pin.length < 4}
            className="flex-1 bg-fuchsia-500 hover:bg-fuchsia-400 disabled:opacity-40 text-white font-black py-3 rounded-full"
          >Unlock</button>
        </div>
      </div>
    </div>
  );
}
